import React, { useEffect } from 'react';

const BloomFilters = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Bloom Filters - Probabilistic Data Structures Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master Bloom Filters for fast membership testing. Learn implementation, applications, and optimization techniques for probabilistic data structures.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master Bloom Filters for fast membership testing. Learn implementation, applications, and optimization techniques for probabilistic data structures.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Bloom Filters: Probabilistic Data Structures</h1>
        <p style={styles.subtitle}>
          Achieve fast membership testing with space-efficient probabilistic data structures
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Introduction to Bloom Filters</h2>
          <p style={styles.paragraph}>
            A Bloom Filter is a space-efficient probabilistic data structure designed to test whether an 
            element is a member of a set. It can definitively say "no" when an element is not in the set, 
            but can only say "possibly" when an element might be in the set. This trade-off between accuracy 
            and space efficiency makes Bloom Filters invaluable for applications where memory is limited 
            and false positives are acceptable.
          </p>
          <p style={styles.paragraph}>
            Bloom Filters are widely used in distributed systems, databases, web crawlers, and caching 
            systems where quick membership tests are needed without the overhead of storing the actual data. 
            They provide constant-time operations and use significantly less memory than traditional hash tables.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>How Bloom Filters Work</h2>
          
          <h3 style={styles.heading}>Basic Structure</h3>
          <p style={styles.paragraph}>
            A Bloom Filter consists of a bit array of size m and k independent hash functions. Each hash 
            function maps elements to one of the m array positions with uniform random distribution.
          </p>
          <pre style={styles.codeBlock}>
{`Bloom Filter Structure:

Bit Array (m bits):
[0][0][0][0][0][0][0][0][0][0][0][0][0][0][0][0]
 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15

Hash Functions (k functions):
- hash1(x) = x % m
- hash2(x) = (x * 31) % m  
- hash3(x) = (x * 37) % m

Operations:
1. ADD(element):
   - Compute hash1(element), hash2(element), ..., hashk(element)
   - Set bits at those positions to 1

2. CONTAINS(element):
   - Compute hash1(element), hash2(element), ..., hashk(element)
   - Check if ALL bits at those positions are 1
   - If any bit is 0: element is definitely NOT in set
   - If all bits are 1: element is POSSIBLY in set

Key Properties:
✅ No false negatives (if says "no", element is definitely not in set)
❌ Possible false positives (if says "yes", element might not be in set)
✅ Constant time operations O(k)
✅ Fixed memory usage regardless of number of elements
✅ Cannot delete elements (standard Bloom Filter)

Example - Adding "hello":
hash1("hello") = 3  → Set bit 3 to 1
hash2("hello") = 7  → Set bit 7 to 1  
hash3("hello") = 12 → Set bit 12 to 1

Bit Array after adding "hello":
[0][0][0][1][0][0][0][1][0][0][0][0][1][0][0][0]
 0  1  2  3  4  5  6  7  8  9 10 11 12 13 14 15

Checking if "hello" exists:
hash1("hello") = 3  → Check bit 3: 1 ✓
hash2("hello") = 7  → Check bit 7: 1 ✓
hash3("hello") = 12 → Check bit 12: 1 ✓
Result: "hello" is POSSIBLY in the set

Checking if "world" exists:
hash1("world") = 5  → Check bit 5: 0 ✗
Result: "world" is DEFINITELY NOT in the set`}
          </pre>

          <h3 style={styles.heading}>False Positive Rate</h3>
          <p style={styles.paragraph}>
            The false positive rate of a Bloom Filter depends on the size of the bit array (m), the number 
            of hash functions (k), and the number of elements added (n). The optimal configuration minimizes 
            this rate while maintaining efficiency.
          </p>
          <pre style={styles.codeBlock}>
{`False Positive Rate Calculation:

Given:
- m = size of bit array
- k = number of hash functions  
- n = number of elements inserted

Probability that a specific bit is still 0 after inserting n elements:
P(bit = 0) = (1 - 1/m)^(kn) ≈ e^(-kn/m)

Probability that a specific bit is 1:
P(bit = 1) = 1 - e^(-kn/m)

False positive rate (all k bits are 1 for non-member):
FPR = (1 - e^(-kn/m))^k

Optimal number of hash functions:
k_optimal = (m/n) * ln(2) ≈ 0.693 * (m/n)

With optimal k, false positive rate becomes:
FPR_optimal = (0.5)^k = 2^(-k) = 2^(-0.693*m/n)

Example Calculations:

Scenario 1: Small filter
- m = 100 bits
- n = 10 elements
- k_optimal = 0.693 * (100/10) = 6.93 ≈ 7
- FPR ≈ 2^(-7) ≈ 0.78% (very low)

Scenario 2: Larger dataset
- m = 1000 bits  
- n = 200 elements
- k_optimal = 0.693 * (1000/200) = 3.47 ≈ 3
- FPR ≈ 2^(-3) ≈ 12.5% (higher but acceptable)

Memory Efficiency Comparison:
┌─────────────────┬─────────────┬──────────────┬─────────────┐
│ Data Structure  │ Memory      │ Lookup Time  │ False Pos   │
├─────────────────┼─────────────┼──────────────┼─────────────┤
│ Hash Set        │ 32-64 bytes │ O(1) avg     │ 0%          │
│ (per element)   │ per element │ O(n) worst   │             │
├─────────────────┼─────────────┼──────────────┼─────────────┤
│ Sorted Array    │ 4-8 bytes   │ O(log n)     │ 0%          │
│ (per element)   │ per element │              │             │
├─────────────────┼─────────────┼──────────────┼─────────────┤
│ Bloom Filter    │ ~10 bits    │ O(k) const   │ 1-15%       │
│ (per element)   │ per element │              │ (tunable)   │
└─────────────────┴─────────────┴──────────────┴─────────────┘

Space Savings Example:
1 million URLs in hash set: ~64MB
1 million URLs in Bloom Filter (1% FPR): ~1.2MB
Space reduction: ~98%`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation</h2>
          
          <h3 style={styles.heading}>Basic Bloom Filter</h3>
          <p style={styles.paragraph}>
            A basic Bloom Filter implementation requires careful selection of hash functions and proper 
            bit manipulation for optimal performance.
          </p>
          <pre style={styles.codeBlock}>
{`Basic Bloom Filter Implementation:

import hashlib
import math
from typing import Union

class BloomFilter:
    def __init__(self, expected_elements: int, false_positive_rate: float = 0.01):
        """
        Initialize Bloom Filter
        
        Args:
            expected_elements: Expected number of elements to be added
            false_positive_rate: Desired false positive rate (0.01 = 1%)
        """
        self.expected_elements = expected_elements
        self.false_positive_rate = false_positive_rate
        
        # Calculate optimal bit array size
        self.bit_array_size = self._calculate_bit_array_size()
        
        # Calculate optimal number of hash functions
        self.num_hash_functions = self._calculate_num_hash_functions()
        
        # Initialize bit array
        self.bit_array = [0] * self.bit_array_size
        
        # Track number of elements added
        self.num_elements = 0
        
        print(f"Bloom Filter initialized:")
        print(f"  Expected elements: {expected_elements}")
        print(f"  Bit array size: {self.bit_array_size}")
        print(f"  Number of hash functions: {self.num_hash_functions}")
        print(f"  Target false positive rate: {false_positive_rate:.2%}")
    
    def _calculate_bit_array_size(self) -> int:
        """Calculate optimal bit array size"""
        m = -(self.expected_elements * math.log(self.false_positive_rate)) / (math.log(2) ** 2)
        return int(math.ceil(m))
    
    def _calculate_num_hash_functions(self) -> int:
        """Calculate optimal number of hash functions"""
        k = (self.bit_array_size / self.expected_elements) * math.log(2)
        return int(math.ceil(k))
    
    def _hash(self, item: Union[str, bytes], seed: int) -> int:
        """Generate hash value for item with given seed"""
        if isinstance(item, str):
            item = item.encode('utf-8')
        
        # Use SHA-256 with seed for good distribution
        hash_input = seed.to_bytes(4, byteorder='big') + item
        hash_digest = hashlib.sha256(hash_input).hexdigest()
        
        # Convert hex to integer and mod by array size
        return int(hash_digest, 16) % self.bit_array_size
    
    def add(self, item: Union[str, bytes]) -> None:
        """Add item to Bloom Filter"""
        for i in range(self.num_hash_functions):
            index = self._hash(item, i)
            self.bit_array[index] = 1
        
        self.num_elements += 1
    
    def contains(self, item: Union[str, bytes]) -> bool:
        """Check if item might be in the set"""
        for i in range(self.num_hash_functions):
            index = self._hash(item, i)
            if self.bit_array[index] == 0:
                return False  # Definitely not in set
        
        return True  # Possibly in set
    
    def __contains__(self, item: Union[str, bytes]) -> bool:
        """Support 'in' operator"""
        return self.contains(item)
    
    def current_false_positive_rate(self) -> float:
        """Calculate current false positive rate based on elements added"""
        if self.num_elements == 0:
            return 0.0
        
        # Probability that a bit is still 0
        p = math.exp(-(self.num_hash_functions * self.num_elements) / self.bit_array_size)
        
        # False positive rate
        return (1 - p) ** self.num_hash_functions
    
    def get_stats(self) -> dict:
        """Get Bloom Filter statistics"""
        bits_set = sum(self.bit_array)
        fill_ratio = bits_set / self.bit_array_size
        
        return {
            'bit_array_size': self.bit_array_size,
            'num_hash_functions': self.num_hash_functions,
            'elements_added': self.num_elements,
            'bits_set': bits_set,
            'fill_ratio': fill_ratio,
            'current_fpr': self.current_false_positive_rate(),
            'memory_usage_bits': self.bit_array_size,
            'memory_usage_bytes': math.ceil(self.bit_array_size / 8)
        }

# Usage Example
def bloom_filter_demo():
    # Create Bloom Filter for 1000 elements with 1% false positive rate
    bf = BloomFilter(expected_elements=1000, false_positive_rate=0.01)
    
    # Add some elements
    words = ['apple', 'banana', 'cherry', 'date', 'elderberry']
    for word in words:
        bf.add(word)
        print(f"Added '{word}'")
    
    # Test membership
    test_words = ['apple', 'grape', 'banana', 'kiwi', 'cherry']
    for word in test_words:
        result = word in bf
        print(f"'{word}' in filter: {result}")
    
    # Show statistics
    stats = bf.get_stats()
    print("\\nBloom Filter Statistics:")
    for key, value in stats.items():
        if isinstance(value, float):
            print(f"  {key}: {value:.4f}")
        else:
            print(f"  {key}: {value}")

# Run demo
bloom_filter_demo()

Output:
Bloom Filter initialized:
  Expected elements: 1000
  Bit array size: 9586
  Number of hash functions: 7
  Target false positive rate: 1.00%
Added 'apple'
Added 'banana'
Added 'cherry'
Added 'date'
Added 'elderberry'
'apple' in filter: True
'grape' in filter: False
'banana' in filter: True
'kiwi' in filter: False
'cherry' in filter: True

Bloom Filter Statistics:
  bit_array_size: 9586
  num_hash_functions: 7
  elements_added: 5
  bits_set: 35
  fill_ratio: 0.0037
  current_fpr: 0.0000
  memory_usage_bits: 9586
  memory_usage_bytes: 1199`}
          </pre>

          <h3 style={styles.heading}>Optimized Implementation</h3>
          <p style={styles.paragraph}>
            For production use, Bloom Filters can be optimized using bit manipulation, better hash functions, 
            and memory-efficient storage formats.
          </p>
          <pre style={styles.codeBlock}>
{`Optimized Bloom Filter Implementation:

import mmh3  # MurmurHash3 - fast, good distribution
import array
import math
from typing import Union, List

class OptimizedBloomFilter:
    def __init__(self, expected_elements: int, false_positive_rate: float = 0.01):
        self.expected_elements = expected_elements
        self.false_positive_rate = false_positive_rate
        
        # Calculate parameters
        self.bit_array_size = self._calculate_bit_array_size()
        self.num_hash_functions = self._calculate_num_hash_functions()
        
        # Use bit array for memory efficiency
        # array.array('B') uses 1 byte per element
        num_bytes = math.ceil(self.bit_array_size / 8)
        self.bit_array = array.array('B', [0] * num_bytes)
        
        self.num_elements = 0
    
    def _calculate_bit_array_size(self) -> int:
        m = -(self.expected_elements * math.log(self.false_positive_rate)) / (math.log(2) ** 2)
        return int(math.ceil(m))
    
    def _calculate_num_hash_functions(self) -> int:
        k = (self.bit_array_size / self.expected_elements) * math.log(2)
        return int(math.ceil(k))
    
    def _get_hash_values(self, item: Union[str, bytes]) -> List[int]:
        """Generate k hash values using double hashing technique"""
        if isinstance(item, str):
            item = item.encode('utf-8')
        
        # Use MurmurHash3 with two different seeds
        hash1 = mmh3.hash(item, seed=0) % self.bit_array_size
        hash2 = mmh3.hash(item, seed=hash1) % self.bit_array_size
        
        # Generate k hash values using double hashing
        hash_values = []
        for i in range(self.num_hash_functions):
            hash_val = (hash1 + i * hash2) % self.bit_array_size
            hash_values.append(hash_val)
        
        return hash_values
    
    def _set_bit(self, index: int) -> None:
        """Set bit at given index"""
        byte_index = index // 8
        bit_index = index % 8
        self.bit_array[byte_index] |= (1 << bit_index)
    
    def _get_bit(self, index: int) -> bool:
        """Get bit at given index"""
        byte_index = index // 8
        bit_index = index % 8
        return bool(self.bit_array[byte_index] & (1 << bit_index))
    
    def add(self, item: Union[str, bytes]) -> None:
        """Add item to Bloom Filter"""
        hash_values = self._get_hash_values(item)
        for hash_val in hash_values:
            self._set_bit(hash_val)
        self.num_elements += 1
    
    def contains(self, item: Union[str, bytes]) -> bool:
        """Check if item might be in the set"""
        hash_values = self._get_hash_values(item)
        return all(self._get_bit(hash_val) for hash_val in hash_values)
    
    def add_batch(self, items: List[Union[str, bytes]]) -> None:
        """Add multiple items efficiently"""
        for item in items:
            self.add(item)
    
    def union(self, other: 'OptimizedBloomFilter') -> 'OptimizedBloomFilter':
        """Create union of two Bloom Filters"""
        if (self.bit_array_size != other.bit_array_size or 
            self.num_hash_functions != other.num_hash_functions):
            raise ValueError("Bloom Filters must have same parameters for union")
        
        result = OptimizedBloomFilter(self.expected_elements, self.false_positive_rate)
        
        # Perform bitwise OR
        for i in range(len(self.bit_array)):
            result.bit_array[i] = self.bit_array[i] | other.bit_array[i]
        
        result.num_elements = self.num_elements + other.num_elements
        return result
    
    def intersection_estimate(self, other: 'OptimizedBloomFilter') -> int:
        """Estimate size of intersection using inclusion-exclusion principle"""
        if (self.bit_array_size != other.bit_array_size or 
            self.num_hash_functions != other.num_hash_functions):
            raise ValueError("Bloom Filters must have same parameters")
        
        # Count bits set in each filter and intersection
        bits_self = sum(bin(byte).count('1') for byte in self.bit_array)
        bits_other = sum(bin(byte).count('1') for byte in other.bit_array)
        bits_intersection = sum(bin(a & b).count('1') for a, b in zip(self.bit_array, other.bit_array))
        
        # Estimate using inclusion-exclusion principle
        c_self = -self.bit_array_size * math.log(1 - bits_self / self.bit_array_size)
        c_other = -self.bit_array_size * math.log(1 - bits_other / self.bit_array_size)
        c_intersection = -self.bit_array_size * math.log(1 - bits_intersection / self.bit_array_size)
        
        return max(0, int(c_self + c_other - c_intersection))
    
    def serialize(self) -> bytes:
        """Serialize Bloom Filter to bytes"""
        import struct
        
        header = struct.pack('!IIII', 
                           self.bit_array_size,
                           self.num_hash_functions, 
                           self.expected_elements,
                           self.num_elements)
        
        fpr_bytes = struct.pack('!d', self.false_positive_rate)
        
        return header + fpr_bytes + self.bit_array.tobytes()
    
    @classmethod
    def deserialize(cls, data: bytes) -> 'OptimizedBloomFilter':
        """Deserialize Bloom Filter from bytes"""
        import struct
        
        # Unpack header
        bit_array_size, num_hash_functions, expected_elements, num_elements = struct.unpack('!IIII', data[:16])
        false_positive_rate = struct.unpack('!d', data[16:24])[0]
        
        # Create filter
        bf = cls(expected_elements, false_positive_rate)
        bf.bit_array_size = bit_array_size
        bf.num_hash_functions = num_hash_functions
        bf.num_elements = num_elements
        
        # Restore bit array
        bit_data = data[24:]
        bf.bit_array = array.array('B', bit_data)
        
        return bf

# Performance comparison
def performance_comparison():
    import time
    import random
    import string
    
    # Generate test data
    def random_string(length=10):
        return ''.join(random.choices(string.ascii_lowercase, k=length))
    
    test_data = [random_string() for _ in range(10000)]
    query_data = [random_string() for _ in range(1000)]
    
    # Test basic implementation
    start_time = time.time()
    bf_basic = BloomFilter(10000, 0.01)
    for item in test_data:
        bf_basic.add(item)
    
    for item in query_data:
        _ = item in bf_basic
    
    basic_time = time.time() - start_time
    
    # Test optimized implementation
    start_time = time.time()
    bf_optimized = OptimizedBloomFilter(10000, 0.01)
    for item in test_data:
        bf_optimized.add(item)
    
    for item in query_data:
        _ = item in bf_optimized
    
    optimized_time = time.time() - start_time
    
    print(f"Basic implementation: {basic_time:.3f}s")
    print(f"Optimized implementation: {optimized_time:.3f}s") 
    print(f"Speedup: {basic_time/optimized_time:.2f}x")
    
    # Memory usage comparison
    basic_memory = bf_basic.get_stats()['memory_usage_bytes']
    optimized_memory = len(bf_optimized.bit_array)
    
    print(f"Basic memory usage: {basic_memory} bytes")
    print(f"Optimized memory usage: {optimized_memory} bytes")
    print(f"Memory reduction: {(basic_memory - optimized_memory) / basic_memory * 100:.1f}%")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Variants</h2>
          
          <h3 style={styles.heading}>Counting Bloom Filter</h3>
          <p style={styles.paragraph}>
            Counting Bloom Filters support deletion by using counters instead of bits, allowing elements 
            to be removed from the filter while maintaining the probabilistic properties.
          </p>
          <pre style={styles.codeBlock}>
{`Counting Bloom Filter Implementation:

import array
from typing import Union, List

class CountingBloomFilter:
    def __init__(self, expected_elements: int, false_positive_rate: float = 0.01, 
                 counter_size: int = 4):
        """
        Counting Bloom Filter with deletion support
        
        Args:
            counter_size: Size of each counter in bits (4 bits = max count 15)
        """
        self.expected_elements = expected_elements
        self.false_positive_rate = false_positive_rate
        self.counter_size = counter_size
        self.max_count = (2 ** counter_size) - 1
        
        # Calculate parameters
        self.bit_array_size = self._calculate_bit_array_size()
        self.num_hash_functions = self._calculate_num_hash_functions()
        
        # Use array of counters instead of bits
        self.counters = array.array('B', [0] * self.bit_array_size)
        self.num_elements = 0
    
    def _calculate_bit_array_size(self) -> int:
        m = -(self.expected_elements * math.log(self.false_positive_rate)) / (math.log(2) ** 2)
        return int(math.ceil(m))
    
    def _calculate_num_hash_functions(self) -> int:
        k = (self.bit_array_size / self.expected_elements) * math.log(2)
        return int(math.ceil(k))
    
    def _get_hash_values(self, item: Union[str, bytes]) -> List[int]:
        """Generate hash values for item"""
        if isinstance(item, str):
            item = item.encode('utf-8')
        
        hash_values = []
        for i in range(self.num_hash_functions):
            hash_val = hash((item, i)) % self.bit_array_size
            hash_values.append(hash_val)
        
        return hash_values
    
    def add(self, item: Union[str, bytes]) -> bool:
        """Add item to filter. Returns False if any counter would overflow."""
        hash_values = self._get_hash_values(item)
        
        # Check for potential overflow
        for hash_val in hash_values:
            if self.counters[hash_val] >= self.max_count:
                return False
        
        # Increment all counters
        for hash_val in hash_values:
            self.counters[hash_val] += 1
        
        self.num_elements += 1
        return True
    
    def remove(self, item: Union[str, bytes]) -> bool:
        """Remove item from filter. Returns False if item wasn't in filter."""
        hash_values = self._get_hash_values(item)
        
        # Check if item is in filter (all counters > 0)
        if not all(self.counters[hash_val] > 0 for hash_val in hash_values):
            return False
        
        # Decrement all counters
        for hash_val in hash_values:
            self.counters[hash_val] -= 1
        
        self.num_elements -= 1
        return True
    
    def contains(self, item: Union[str, bytes]) -> bool:
        """Check if item might be in the set"""
        hash_values = self._get_hash_values(item)
        return all(self.counters[hash_val] > 0 for hash_val in hash_values)
    
    def get_load_factor(self) -> float:
        """Get average counter value"""
        return sum(self.counters) / len(self.counters)

# Usage example
cbf = CountingBloomFilter(1000, 0.01)

# Add elements
items = ['apple', 'banana', 'cherry']
for item in items:
    cbf.add(item)
    print(f"Added {item}")

# Check membership
for item in ['apple', 'grape', 'banana']:
    print(f"{item} in filter: {cbf.contains(item)}")

# Remove element
cbf.remove('apple')
print(f"After removing apple: {cbf.contains('apple')}")

# Add duplicate (increases counters)
cbf.add('banana')
print(f"After adding banana again: {cbf.contains('banana')}")
cbf.remove('banana')
print(f"After removing banana once: {cbf.contains('banana')}")
cbf.remove('banana')
print(f"After removing banana twice: {cbf.contains('banana')}")`}
          </pre>

          <h3 style={styles.heading}>Scalable Bloom Filter</h3>
          <p style={styles.paragraph}>
            Scalable Bloom Filters automatically grow as more elements are added, maintaining a target 
            false positive rate by adding new filter layers when capacity is reached.
          </p>
          <pre style={styles.codeBlock}>
{`Scalable Bloom Filter Implementation:

class ScalableBloomFilter:
    def __init__(self, initial_capacity: int = 1000, 
                 false_positive_rate: float = 0.01,
                 growth_factor: int = 2,
                 tightening_ratio: float = 0.9):
        """
        Scalable Bloom Filter that grows automatically
        
        Args:
            initial_capacity: Initial capacity of first filter
            false_positive_rate: Target false positive rate
            growth_factor: How much to grow each new filter
            tightening_ratio: How much to tighten FPR for each new filter
        """
        self.initial_capacity = initial_capacity
        self.base_false_positive_rate = false_positive_rate
        self.growth_factor = growth_factor
        self.tightening_ratio = tightening_ratio
        
        # List of individual Bloom Filters
        self.filters = []
        self.capacities = []
        self.false_positive_rates = []
        
        # Create first filter
        self._add_new_filter()
    
    def _add_new_filter(self) -> None:
        """Add a new Bloom Filter to the scalable filter"""
        if not self.filters:
            # First filter
            capacity = self.initial_capacity
            fpr = self.base_false_positive_rate
        else:
            # Subsequent filters
            capacity = self.capacities[-1] * self.growth_factor
            fpr = self.false_positive_rates[-1] * self.tightening_ratio
        
        new_filter = OptimizedBloomFilter(capacity, fpr)
        self.filters.append(new_filter)
        self.capacities.append(capacity)
        self.false_positive_rates.append(fpr)
        
        print(f"Added new filter #{len(self.filters)}: capacity={capacity}, fpr={fpr:.6f}")
    
    def add(self, item: Union[str, bytes]) -> None:
        """Add item to the scalable filter"""
        # Always add to the most recent filter
        current_filter = self.filters[-1]
        current_filter.add(item)
        
        # Check if we need to add a new filter
        if current_filter.num_elements >= self.capacities[-1]:
            self._add_new_filter()
    
    def contains(self, item: Union[str, bytes]) -> bool:
        """Check if item might be in any of the filters"""
        return any(bf.contains(item) for bf in self.filters)
    
    def __contains__(self, item: Union[str, bytes]) -> bool:
        return self.contains(item)
    
    def get_overall_false_positive_rate(self) -> float:
        """Calculate overall false positive rate"""
        # FPR = 1 - ∏(1 - FPR_i) for all filters
        overall_fpr = 1.0
        for fpr in self.false_positive_rates:
            overall_fpr *= (1.0 - fpr)
        return 1.0 - overall_fpr
    
    def get_stats(self) -> dict:
        """Get comprehensive statistics"""
        total_elements = sum(bf.num_elements for bf in self.filters)
        total_memory = sum(len(bf.bit_array) for bf in self.filters)
        
        return {
            'num_filters': len(self.filters),
            'total_elements': total_elements,
            'total_memory_bytes': total_memory,
            'overall_fpr': self.get_overall_false_positive_rate(),
            'filter_details': [
                {
                    'filter_id': i,
                    'capacity': self.capacities[i],
                    'elements': self.filters[i].num_elements,
                    'fpr': self.false_positive_rates[i],
                    'memory_bytes': len(self.filters[i].bit_array)
                }
                for i in range(len(self.filters))
            ]
        }

# Usage example
sbf = ScalableBloomFilter(initial_capacity=100, false_positive_rate=0.01)

# Add many elements to trigger scaling
elements = [f"element_{i}" for i in range(500)]
for element in elements:
    sbf.add(element)

# Check membership
test_elements = ['element_0', 'element_50', 'element_499', 'not_in_filter']
for element in test_elements:
    result = element in sbf
    print(f"'{element}' in filter: {result}")

# Show statistics
stats = sbf.get_stats()
print(f"\\nScalable Bloom Filter Stats:")
print(f"Number of filters: {stats['num_filters']}")
print(f"Total elements: {stats['total_elements']}")
print(f"Total memory: {stats['total_memory_bytes']} bytes")
print(f"Overall FPR: {stats['overall_fpr']:.6f}")

for filter_info in stats['filter_details']:
    print(f"Filter {filter_info['filter_id']}: "
          f"{filter_info['elements']}/{filter_info['capacity']} elements, "
          f"FPR: {filter_info['fpr']:.6f}")`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Applications</h2>
          
          <h3 style={styles.heading}>Database Query Optimization</h3>
          <p style={styles.paragraph}>
            Bloom Filters are widely used in databases to avoid expensive disk reads by quickly determining 
            if a key definitely doesn't exist in a data file or partition.
          </p>
          <pre style={styles.codeBlock}>
{`Database Bloom Filter Usage:

class DatabaseIndex:
    def __init__(self, sst_file_path: str):
        self.sst_file_path = sst_file_path
        self.bloom_filter = None
        self.key_count = 0
        self._build_bloom_filter()
    
    def _build_bloom_filter(self):
        """Build Bloom Filter for SST file keys"""
        # Read all keys from SST file
        keys = self._read_keys_from_sst()
        self.key_count = len(keys)
        
        # Create Bloom Filter with 1% false positive rate
        self.bloom_filter = OptimizedBloomFilter(
            expected_elements=self.key_count,
            false_positive_rate=0.01
        )
        
        # Add all keys to filter
        for key in keys:
            self.bloom_filter.add(key)
        
        print(f"Built Bloom Filter for {self.key_count} keys")
        print(f"Memory usage: {len(self.bloom_filter.bit_array)} bytes")
    
    def _read_keys_from_sst(self) -> List[str]:
        """Read keys from SST file (simplified)"""
        # In real implementation, this would read from actual SST file
        return [f"key_{i}" for i in range(10000)]
    
    def might_contain_key(self, key: str) -> bool:
        """Check if key might exist in SST file"""
        return self.bloom_filter.contains(key)
    
    def get_value(self, key: str) -> str:
        """Get value for key (with Bloom Filter optimization)"""
        # First check Bloom Filter
        if not self.might_contain_key(key):
            return None  # Definitely not in file
        
        # Key might be in file, perform expensive disk read
        return self._read_from_disk(key)
    
    def _read_from_disk(self, key: str) -> str:
        """Simulate expensive disk read"""
        print(f"Performing disk read for key: {key}")
        # In real implementation, this would read from SST file
        if key.startswith("key_"):
            return f"value_for_{key}"
        return None

# LSM-Tree with Bloom Filters
class LSMTree:
    def __init__(self):
        self.sst_files = []
        self.memtable = {}
    
    def put(self, key: str, value: str):
        """Insert key-value pair"""
        self.memtable[key] = value
        
        # Flush to SST when memtable is full
        if len(self.memtable) >= 1000:
            self._flush_memtable()
    
    def get(self, key: str) -> str:
        """Get value for key"""
        # Check memtable first
        if key in self.memtable:
            return self.memtable[key]
        
        # Check SST files (newest first)
        for sst_file in reversed(self.sst_files):
            if sst_file.might_contain_key(key):
                value = sst_file.get_value(key)
                if value is not None:
                    return value
        
        return None
    
    def _flush_memtable(self):
        """Flush memtable to new SST file"""
        sst_file = DatabaseIndex(f"sst_{len(self.sst_files)}.db")
        self.sst_files.append(sst_file)
        self.memtable.clear()

# Web Crawler Duplicate Detection
class WebCrawler:
    def __init__(self):
        self.visited_urls = ScalableBloomFilter(
            initial_capacity=100000,
            false_positive_rate=0.001  # Very low FPR for crawling
        )
        self.url_queue = []
        self.crawled_count = 0
    
    def add_url(self, url: str) -> bool:
        """Add URL to crawl queue if not already visited"""
        if url in self.visited_urls:
            return False  # Probably already visited
        
        self.url_queue.append(url)
        self.visited_urls.add(url)
        return True
    
    def crawl_url(self, url: str) -> List[str]:
        """Crawl URL and return found links"""
        print(f"Crawling: {url}")
        self.crawled_count += 1
        
        # Simulate finding links
        found_links = [f"{url}/page_{i}" for i in range(5)]
        
        # Add new links to queue
        new_links = 0
        for link in found_links:
            if self.add_url(link):
                new_links += 1
        
        print(f"Found {new_links} new links")
        return found_links
    
    def crawl(self, seed_urls: List[str], max_pages: int = 1000):
        """Main crawling loop"""
        # Add seed URLs
        for url in seed_urls:
            self.add_url(url)
        
        while self.url_queue and self.crawled_count < max_pages:
            url = self.url_queue.pop(0)
            self.crawl_url(url)
        
        stats = self.visited_urls.get_stats()
        print(f"\\nCrawling complete:")
        print(f"Pages crawled: {self.crawled_count}")
        print(f"URLs in filter: {stats['total_elements']}")
        print(f"Memory usage: {stats['total_memory_bytes'] / 1024:.1f} KB")

# Cache Miss Reduction
class BloomFilterCache:
    def __init__(self, cache_size: int = 1000):
        self.cache = {}
        self.cache_size = cache_size
        self.bloom_filter = OptimizedBloomFilter(
            expected_elements=cache_size,
            false_positive_rate=0.01
        )
        self.hits = 0
        self.misses = 0
        self.bloom_saves = 0
    
    def get(self, key: str) -> str:
        """Get value from cache"""
        # First check Bloom Filter
        if not self.bloom_filter.contains(key):
            # Definitely not in cache
            self.bloom_saves += 1
            self.misses += 1
            return None
        
        # Key might be in cache
        if key in self.cache:
            self.hits += 1
            return self.cache[key]
        else:
            # False positive
            self.misses += 1
            return None
    
    def put(self, key: str, value: str):
        """Put value in cache"""
        if len(self.cache) >= self.cache_size:
            # Simple eviction: remove oldest
            oldest_key = next(iter(self.cache))
            del self.cache[oldest_key]
        
        self.cache[key] = value
        self.bloom_filter.add(key)
    
    def get_stats(self) -> dict:
        """Get cache statistics"""
        total_requests = self.hits + self.misses
        hit_rate = self.hits / total_requests if total_requests > 0 else 0
        
        return {
            'hits': self.hits,
            'misses': self.misses,
            'hit_rate': hit_rate,
            'bloom_saves': self.bloom_saves,
            'false_positives': self.misses - self.bloom_saves
        }`}
          </pre>

          <h3 style={styles.heading}>Distributed Systems</h3>
          <p style={styles.paragraph}>
            In distributed systems, Bloom Filters help reduce network communication by allowing nodes 
            to quickly determine if they need to query other nodes for data.
          </p>
          <pre style={styles.codeBlock}>
{`Distributed System Applications:

# Distributed Hash Table with Bloom Filters
class DHTNode:
    def __init__(self, node_id: str, peer_nodes: List[str]):
        self.node_id = node_id
        self.peer_nodes = peer_nodes
        self.local_data = {}
        
        # Bloom Filter for local keys
        self.local_keys_filter = OptimizedBloomFilter(10000, 0.01)
        
        # Bloom Filters for peer nodes (received via gossip)
        self.peer_filters = {}
    
    def put(self, key: str, value: str):
        """Store key-value pair locally"""
        self.local_data[key] = value
        self.local_keys_filter.add(key)
    
    def get(self, key: str) -> str:
        """Get value for key (local or remote)"""
        # Check local data first
        if key in self.local_data:
            return self.local_data[key]
        
        # Check which peers might have the key
        candidate_peers = []
        for peer_id, peer_filter in self.peer_filters.items():
            if peer_filter.contains(key):
                candidate_peers.append(peer_id)
        
        # Query candidate peers
        for peer_id in candidate_peers:
            value = self._query_peer(peer_id, key)
            if value is not None:
                return value
        
        return None
    
    def _query_peer(self, peer_id: str, key: str) -> str:
        """Query peer node for key"""
        print(f"Querying peer {peer_id} for key {key}")
        # In real implementation, this would be a network call
        return None
    
    def exchange_bloom_filters(self):
        """Exchange Bloom Filters with peers (gossip protocol)"""
        for peer_id in self.peer_nodes:
            # Send our filter to peer
            self._send_bloom_filter_to_peer(peer_id, self.local_keys_filter)
            
            # Receive filter from peer
            peer_filter = self._receive_bloom_filter_from_peer(peer_id)
            if peer_filter:
                self.peer_filters[peer_id] = peer_filter

# Content Distribution Network
class CDNNode:
    def __init__(self, node_id: str, cache_size: int = 10000):
        self.node_id = node_id
        self.cache = {}
        self.cache_size = cache_size
        
        # Bloom Filter for cached content
        self.cache_filter = OptimizedBloomFilter(cache_size, 0.01)
        
        # Bloom Filters from other CDN nodes
        self.peer_cache_filters = {}
    
    def cache_content(self, content_id: str, content: bytes):
        """Cache content locally"""
        if len(self.cache) >= self.cache_size:
            # Evict least recently used
            oldest_id = next(iter(self.cache))
            del self.cache[oldest_id]
        
        self.cache[content_id] = content
        self.cache_filter.add(content_id)
    
    def get_content(self, content_id: str) -> bytes:
        """Get content from local cache or peers"""
        # Check local cache
        if content_id in self.cache:
            return self.cache[content_id]
        
        # Find peers that might have the content
        candidate_peers = []
        for peer_id, peer_filter in self.peer_cache_filters.items():
            if peer_filter.contains(content_id):
                candidate_peers.append(peer_id)
        
        # Sort by network distance/latency
        candidate_peers.sort(key=lambda p: self._get_peer_latency(p))
        
        # Try to fetch from closest peer
        for peer_id in candidate_peers:
            content = self._fetch_from_peer(peer_id, content_id)
            if content:
                # Cache locally for future requests
                self.cache_content(content_id, content)
                return content
        
        # Fetch from origin server as last resort
        return self._fetch_from_origin(content_id)
    
    def _get_peer_latency(self, peer_id: str) -> float:
        """Get network latency to peer"""
        # Simplified latency calculation
        return hash(peer_id) % 100
    
    def sync_bloom_filters(self):
        """Synchronize Bloom Filters with other CDN nodes"""
        # In real implementation, this would use efficient gossip protocol
        pass

# Anti-Spam System
class AntiSpamFilter:
    def __init__(self):
        # Known spam patterns
        self.spam_patterns = OptimizedBloomFilter(1000000, 0.001)
        
        # Known good senders
        self.whitelist = OptimizedBloomFilter(100000, 0.001)
        
        # Recently seen message hashes (duplicate detection)
        self.recent_messages = OptimizedBloomFilter(10000, 0.01)
        
        self._load_spam_patterns()
        self._load_whitelist()
    
    def _load_spam_patterns(self):
        """Load known spam patterns"""
        # In real implementation, load from spam database
        spam_patterns = [
            "buy now", "limited time", "act fast", "free money",
            "click here", "guaranteed", "no risk", "urgent"
        ]
        for pattern in spam_patterns:
            self.spam_patterns.add(pattern.lower())
    
    def _load_whitelist(self):
        """Load trusted senders"""
        # In real implementation, load from trusted sender database
        trusted_domains = [
            "@company.com", "@bank.com", "@government.gov"
        ]
        for domain in trusted_domains:
            self.whitelist.add(domain.lower())
    
    def is_spam(self, message: dict) -> bool:
        """Check if message is likely spam"""
        sender = message.get('sender', '').lower()
        subject = message.get('subject', '').lower()
        body = message.get('body', '').lower()
        
        # Check whitelist first
        if any(self.whitelist.contains(domain) for domain in [sender]):
            return False
        
        # Check for duplicate message
        message_hash = hash(body)
        if self.recent_messages.contains(str(message_hash)):
            return True  # Likely duplicate spam
        
        self.recent_messages.add(str(message_hash))
        
        # Check for spam patterns
        text_to_check = f"{subject} {body}"
        spam_indicators = 0
        
        for word in text_to_check.split():
            if self.spam_patterns.contains(word):
                spam_indicators += 1
        
        # If multiple spam indicators found, classify as spam
        return spam_indicators >= 3
    
    def update_spam_patterns(self, new_patterns: List[str]):
        """Update spam patterns (could trigger filter rebuild)"""
        for pattern in new_patterns:
            self.spam_patterns.add(pattern.lower())

# Usage examples
def distributed_systems_demo():
    # DHT example
    node1 = DHTNode("node1", ["node2", "node3"])
    node1.put("key1", "value1")
    node1.put("key2", "value2")
    
    # CDN example
    cdn_node = CDNNode("cdn_east_1")
    cdn_node.cache_content("video123", b"video_data")
    
    # Anti-spam example
    spam_filter = AntiSpamFilter()
    
    test_messages = [
        {
            "sender": "user@company.com",
            "subject": "Meeting tomorrow",
            "body": "Don't forget about our meeting tomorrow at 2 PM"
        },
        {
            "sender": "spam@badsite.com", 
            "subject": "Buy now! Limited time offer!",
            "body": "Click here for free money! Act fast! Guaranteed results!"
        }
    ]
    
    for msg in test_messages:
        is_spam = spam_filter.is_spam(msg)
        print(f"Message from {msg['sender']}: {'SPAM' if is_spam else 'LEGITIMATE'}")

distributed_systems_demo()`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose parameters carefully:</strong> Calculate optimal bit array size and number 
              of hash functions based on expected elements and desired false positive rate.
            </li>
            <li style={styles.listItem}>
              <strong>Use quality hash functions:</strong> Employ well-distributed hash functions like 
              MurmurHash3 or SHA-256 to minimize clustering and ensure uniform distribution.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor false positive rates:</strong> Track actual false positive rates in production 
              and adjust parameters if they exceed acceptable thresholds.
            </li>
            <li style={styles.listItem}>
              <strong>Consider variants for specific needs:</strong> Use Counting Bloom Filters when deletion 
              is needed, or Scalable Bloom Filters when the dataset size is unknown.
            </li>
            <li style={styles.listItem}>
              <strong>Optimize for your use case:</strong> Balance memory usage, computation cost, and 
              accuracy requirements based on your specific application needs.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for serialization:</strong> Implement efficient serialization for distributed 
              systems and persistent storage requirements.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Bloom Filters are powerful probabilistic data structures that provide space-efficient membership 
            testing with tunable accuracy. They excel in scenarios where memory is limited, fast lookups are 
            required, and occasional false positives are acceptable.
          </p>
          <p style={styles.paragraph}>
            Understanding the mathematical foundations, implementation details, and various applications of 
            Bloom Filters enables you to leverage them effectively in distributed systems, databases, caches, 
            and other performance-critical applications where traditional data structures would be too expensive.
          </p>
        </section>
      </main>
    </article>
  );
};

export default BloomFilters;
